import { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useApplicantContext } from '../contexts/ApplicantContext';
import { Card, Badge, Button, Input, Label } from '../components/ui';
import { PageHeader } from '../components/PageHeader';
import { MajorPicker } from '../components/MajorPicker';
import { SchoolProfile } from '../components/SchoolProfile';
import {
  loadDiscoveryDataset,
  filterSchools,
  defaultFilters,
  statesInDataset,
  type DiscoveryFilters,
  type NumericRange,
} from '../lib/schoolDiscovery';
import {
  SCHOOL_TYPE_LABELS,
  localeBucket,
} from '../lib/schoolDiscoveryTypes';
import type { DiscoverySchool, DiscoveryDataset } from '../lib/schoolDiscoveryTypes';
import {
  addDiscoverySchoolAsTarget,
  findExistingTargetSchoolId,
} from '../lib/addDiscoverySchoolAsTarget';
import { MapPin, SlidersHorizontal, X } from 'lucide-react';
import { format } from 'date-fns';

// =============================================================================
// SchoolsPage — School Discovery (v0.2.32)
//
// Browse all ~6,300 US institutions, filter client-side, view a school's
// profile, and add promising ones as target schools. The dataset is the
// bundled static asset; everything here runs in the browser.
// =============================================================================

const SETTINGS = ['City', 'Suburb', 'Town', 'Rural'];
const PAGE_SIZE = 50;

function pct(v: number | null): string {
  return v == null ? '—' : `${Math.round(v * 100)}%`;
}
function money(v: number | null): string {
  return v == null ? '—' : '$' + v.toLocaleString();
}

/** A compact numeric-range control (min/max + include-unknown). */
function RangeControl({
  label, range, onChange, step, placeholder,
}: {
  label: string;
  range: NumericRange;
  onChange: (r: NumericRange) => void;
  step?: number;
  placeholder?: [string, string];
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-1.5">
        <Input
          type="number"
          step={step}
          value={range.min ?? ''}
          placeholder={placeholder?.[0] ?? 'min'}
          onChange={(e) =>
            onChange({ ...range, min: e.target.value === '' ? null : Number(e.target.value) })
          }
        />
        <span className="text-soft text-sm">–</span>
        <Input
          type="number"
          step={step}
          value={range.max ?? ''}
          placeholder={placeholder?.[1] ?? 'max'}
          onChange={(e) =>
            onChange({ ...range, max: e.target.value === '' ? null : Number(e.target.value) })
          }
        />
      </div>
      <label className="mt-1 flex items-center gap-1.5 text-xs text-soft">
        <input
          type="checkbox"
          checked={range.includeUnknown}
          onChange={(e) => onChange({ ...range, includeUnknown: e.target.checked })}
        />
        Include schools that don't report this
      </label>
    </div>
  );
}

export function SchoolsPage() {
  const { membership } = useAuth();
  const { selectedApplicantId } = useApplicantContext();

  const [dataset, setDataset] = useState<DiscoveryDataset | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DiscoveryFilters>(defaultFilters());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selected, setSelected] = useState<DiscoverySchool | null>(null);
  const [showFilters, setShowFilters] = useState(true);

  // Add-as-target state.
  const [addingId, setAddingId] = useState<number | null>(null);
  const [targetIds, setTargetIds] = useState<Record<number, string>>({});

  useEffect(() => {
    loadDiscoveryDataset()
      .then(setDataset)
      .catch((e) => setLoadError(e instanceof Error ? e.message : String(e)));
  }, []);

  // Reset paging whenever the filters change.
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [filters]);

  const results = useMemo(() => {
    if (!dataset) return [];
    return filterSchools(dataset.schools, filters);
  }, [dataset, filters]);

  const states = useMemo(
    () => (dataset ? statesInDataset(dataset.schools) : []),
    [dataset]
  );

  function patch(p: Partial<DiscoveryFilters>) {
    setFilters((f) => ({ ...f, ...p }));
  }
  function toggleInArray<T>(arr: T[], value: T): T[] {
    return arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
  }

  async function checkAndAdd(s: DiscoverySchool) {
    if (!db || !membership || !selectedApplicantId) return;
    setAddingId(s.id);
    try {
      const existing = await findExistingTargetSchoolId(
        db, membership.householdId, selectedApplicantId, s.id
      );
      if (existing) {
        setTargetIds((m) => ({ ...m, [s.id]: existing }));
        return;
      }
      const newId = await addDiscoverySchoolAsTarget(
        db, membership.householdId, selectedApplicantId, s
      );
      setTargetIds((m) => ({ ...m, [s.id]: newId }));
    } catch (e) {
      alert('Could not add school: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setAddingId(null);
    }
  }

  // When a school's profile opens, check whether it's already a target.
  useEffect(() => {
    if (!selected || !db || !membership || !selectedApplicantId) return;
    if (targetIds[selected.id] !== undefined) return;
    findExistingTargetSchoolId(db, membership.householdId, selectedApplicantId, selected.id)
      .then((existing) => {
        if (existing) setTargetIds((m) => ({ ...m, [selected.id]: existing }));
      })
      .catch(() => {});
  }, [selected, membership, selectedApplicantId, targetIds]);

  if (loadError) {
    return (
      <div className="space-y-4">
        <PageHeader title="Schools" description="Browse and compare US colleges." />
        <Card className="p-6 text-center">
          <p className="text-sm text-rose-700">Couldn't load school data: {loadError}</p>
          <p className="mt-1 text-xs text-soft">Check your connection and refresh.</p>
        </Card>
      </div>
    );
  }

  if (!dataset) {
    return (
      <div className="space-y-4">
        <PageHeader title="Schools" description="Browse and compare US colleges." />
        <div className="text-soft">Loading school data…</div>
      </div>
    );
  }

  const visible = results.slice(0, visibleCount);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Schools"
        description="Browse and compare US colleges, then add ones that fit as target schools."
      />

      {/* Filter toggle + result count */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-soft hover:text-ink"
        >
          <SlidersHorizontal size={14} />
          {showFilters ? 'Hide filters' : 'Show filters'}
        </button>
        <span className="text-sm text-soft">
          {results.length.toLocaleString()} of {dataset.school_count.toLocaleString()} schools
        </span>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <Card className="p-4 space-y-4">
          {/* Text search */}
          <div>
            <Label>Search by name or city</Label>
            <Input
              type="text"
              value={filters.text}
              onChange={(e) => patch({ text: e.target.value })}
              placeholder="e.g. High Point, or Charlotte"
            />
          </div>

          {/* School type */}
          <div>
            <Label>School type</Label>
            <div className="flex flex-wrap gap-1.5">
              {[3, 2, 1, 4, 0].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => patch({ schoolTypes: toggleInArray(filters.schoolTypes, t) })}
                  className={`rounded-md border px-2.5 py-1 text-xs ${
                    filters.schoolTypes.includes(t)
                      ? 'border-forest-300 bg-forest-50 text-forest-800'
                      : 'border-line text-soft'
                  }`}
                >
                  {SCHOOL_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-soft">
              Defaults to four-year colleges. Tap to add community, trade, or graduate schools.
            </p>
          </div>

          {/* Major */}
          <div>
            <Label>Major / field of study</Label>
            <MajorPicker
              family={filters.majorFamily}
              code={filters.majorCode}
              onChange={({ family, code }) => patch({ majorFamily: family, majorCode: code })}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* State */}
            <div>
              <Label>State</Label>
              <select
                multiple
                value={filters.states}
                onChange={(e) =>
                  patch({ states: Array.from(e.target.selectedOptions, (o) => o.value) })
                }
                className="w-full rounded-md border border-line px-2 py-1 text-sm h-24"
              >
                {states.map((st) => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
              <p className="mt-0.5 text-xs text-soft">Ctrl/Cmd-click for multiple.</p>
            </div>

            {/* Setting */}
            <div>
              <Label>Setting</Label>
              <div className="flex flex-wrap gap-1.5">
                {SETTINGS.map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => patch({ settings: toggleInArray(filters.settings, st) })}
                    className={`rounded-md border px-2.5 py-1 text-xs ${
                      filters.settings.includes(st)
                        ? 'border-forest-300 bg-forest-50 text-forest-800'
                        : 'border-line text-soft'
                    }`}
                  >
                    {st}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <RangeControl
              label="Admission rate (%)"
              range={{
                min: filters.admitRate.min != null ? filters.admitRate.min * 100 : null,
                max: filters.admitRate.max != null ? filters.admitRate.max * 100 : null,
                includeUnknown: filters.admitRate.includeUnknown,
              }}
              onChange={(r) =>
                patch({
                  admitRate: {
                    min: r.min != null ? r.min / 100 : null,
                    max: r.max != null ? r.max / 100 : null,
                    includeUnknown: r.includeUnknown,
                  },
                })
              }
              placeholder={['0', '100']}
            />
            <RangeControl
              label="SAT average"
              range={filters.satAvg}
              onChange={(r) => patch({ satAvg: r })}
              placeholder={['400', '1600']}
            />
            <RangeControl
              label="Avg. net price ($)"
              range={filters.netPrice}
              onChange={(r) => patch({ netPrice: r })}
              placeholder={['0', '80000']}
            />
            <RangeControl
              label="Undergrad enrollment"
              range={filters.size}
              onChange={(r) => patch({ size: r })}
              placeholder={['0', '50000']}
            />
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-1.5 text-sm text-soft">
              <input
                type="checkbox"
                checked={filters.religiousOnly}
                onChange={(e) => patch({ religiousOnly: e.target.checked })}
              />
              Religious affiliation only
            </label>
            <label className="flex items-center gap-1.5 text-sm text-soft">
              <input
                type="checkbox"
                checked={filters.hsiOnly}
                onChange={(e) => patch({ hsiOnly: e.target.checked })}
              />
              Hispanic-Serving Institutions only
            </label>
          </div>

          <div className="flex items-center justify-between border-t border-line pt-3">
            <button
              type="button"
              onClick={() => setFilters(defaultFilters())}
              className="text-xs text-soft hover:text-ink"
            >
              Reset filters
            </button>
            <span className="text-xs text-soft">
              Data as of {format(new Date(dataset.generated_at), 'MMM d, yyyy')}
            </span>
          </div>
        </Card>
      )}

      {/* Results */}
      {results.length === 0 ? (
        <Card className="p-6 text-center text-soft text-sm">
          No schools match these filters. Try widening them.
        </Card>
      ) : (
        <div className="space-y-2">
          {visible.map((s) => (
            <Card key={s.id} className="p-3">
              <button
                type="button"
                onClick={() => setSelected(s)}
                className="block w-full text-left"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <span className="font-medium text-ink">{s.name}</span>
                    <p className="text-xs text-soft flex items-center gap-1">
                      <MapPin size={11} />
                      {[s.city, s.state].filter(Boolean).join(', ') || 'Location not reported'}
                      {localeBucket(s.locale) && <> · {localeBucket(s.locale)}</>}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {s.degreePredominant != null && SCHOOL_TYPE_LABELS[s.degreePredominant] && (
                      <Badge variant="indigo">{SCHOOL_TYPE_LABELS[s.degreePredominant]}</Badge>
                    )}
                    {s.hsi === 1 && <Badge variant="green">HSI</Badge>}
                    {targetIds[s.id] && <Badge variant="purple">Target school</Badge>}
                  </div>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-soft">
                  <span>Admit: {pct(s.admitRate)}</span>
                  <span>Net price: {money(s.netPrice)}</span>
                  <span>Grad rate: {pct(s.gradRate)}</span>
                  <span>Enrollment: {s.size?.toLocaleString() ?? '—'}</span>
                </div>
              </button>
            </Card>
          ))}

          {visibleCount < results.length && (
            <div className="text-center pt-1">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
              >
                Show more ({(results.length - visibleCount).toLocaleString()} remaining)
              </Button>
            </div>
          )}
        </div>
      )}

      {/* School profile modal */}
      {selected && (
        <div
          className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
          onClick={() => setSelected(null)}
        >
          <div
            className="mt-8 w-full max-w-2xl rounded-lg bg-paper p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <SchoolProfile
              school={selected}
              alreadyTargetId={targetIds[selected.id] ?? null}
              adding={addingId === selected.id}
              onAddAsTarget={() => checkAndAdd(selected)}
              onClose={() => setSelected(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
